import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import {
  useGetUserAssignments,
  useAssignProject,
  useUnassignProject,
  useReviewApplication,
  useUpdateUserProjectStatus,
} from "../../lib/api/adminProjects";
import {
  useSendProfileReminder,
  useSendTaskReminder,
  useSuspendUser,
  useTerminateUser,
  useReactivateUser,
  useFetchUserDetails,
} from "../../lib/api/adminUsers";

// Components
import UserProfileCard from "../../components/admin/users/UserProfileCard";
import AdminActionsCard from "../../components/admin/users/AdminActionsCard";
import UserAssignmentsList from "../../components/admin/users/UserAssignmentsList";
import AssignmentDropdownMenu from "../../components/admin/users/AssignmentDropdownMenu";
import AssignProjectModal from "../../components/admin/users/AssignProjectModal";
import AssignmentActionModal from "../../components/admin/users/AssignmentActionModal";
import SuspendUserModal from "../../components/admin/users/SuspendUserModal";
import TerminateUserModal from "../../components/admin/users/TerminateUserModal";
import { UserAssignment } from "../../lib/types/adminProjects";

const UserDetails: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<UserAssignment | null>(null);
  const [showActionModal, setShowActionModal] = useState<string | null>(null);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);

  // Form states
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [suspendReason, setSuspendReason] = useState("");
  const [terminateReason, setTerminateReason] = useState("");

  // Menu states
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Fetch user details
  const {
    data: userData,
    isLoading: userLoading,
    refetch: refetchUser,
  } = useFetchUserDetails(userId as string);
  const user = userData;

  // Fetch user's assignments
  const { data: assignmentsData, isLoading: assignmentsLoading } =
    useGetUserAssignments(userId, {
      status: statusFilter || undefined,
    });
  const assignments = assignmentsData?.data?.assignments || [];

  // Project mutations
  const { mutateAsync: assignProject, isPending: isAssigning } =
    useAssignProject();
  const { mutateAsync: unassignProject, isPending: isUnassigning } =
    useUnassignProject();
  const { mutateAsync: reviewApplication, isPending: isReviewing } =
    useReviewApplication();
  const { mutateAsync: updateStatus, isPending: isUpdatingStatus } =
    useUpdateUserProjectStatus();

  // Admin action mutations
  const { mutateAsync: sendProfileReminder, isPending: isSendingReminder } =
    useSendProfileReminder();
  const { mutateAsync: sendTaskReminder, isPending: isSendingTaskReminder } =
    useSendTaskReminder();
  const { mutateAsync: suspendUser, isPending: isSuspending } =
    useSuspendUser();
  const { mutateAsync: terminateUser, isPending: isTerminating } =
    useTerminateUser();
  const { mutateAsync: reactivateUser, isPending: isReactivating } =
    useReactivateUser();

  // Calculate profile completion
  const getVerificationCount = () => {
    if (!user?.verification) return 0;
    return [
      user.verification.emailVerified,
      user.verification.addressVerified,
      user.verification.ssnVerified,
      user.verification.documentVerified,
    ].filter(Boolean).length;
  };

  const profileCompletion = getVerificationCount();
  const isProfileIncomplete = profileCompletion < 4;

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Close menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (openMenuId) {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [openMenuId]);

  // Admin action handlers
  const handleSendProfileReminder = async () => {
    try {
      await sendProfileReminder({ userId: userId! });
    } catch {
      // Error handled by mutation
    }
  };

  const handleSendTaskReminder = async () => {
    try {
      await sendTaskReminder({ userId: userId! });
    } catch {
      // Error handled by mutation
    }
  };

  const handleSuspendUser = async () => {
    try {
      await suspendUser({ userId: userId!, reason: suspendReason });
      setShowSuspendModal(false);
      setSuspendReason("");
      refetchUser();
    } catch {
      // Error handled by mutation
    }
  };

  const handleTerminateUser = async () => {
    try {
      await terminateUser({ userId: userId!, reason: terminateReason });
      setShowTerminateModal(false);
      setTerminateReason("");
      refetchUser();
    } catch {
      // Error handled by mutation
    }
  };

  const handleReactivateUser = async () => {
    try {
      await reactivateUser({ userId: userId! });
      refetchUser();
    } catch {
      // Error handled by mutation
    }
  };

  // Menu handlers
  const handleOpenMenu = (
    assignmentId: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.right - 192,
    });
    setOpenMenuId(openMenuId === assignmentId ? null : assignmentId);
  };

  const handleCloseMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  const handleMenuAction = (type: string) => {
    const assignment = assignments.find((a) => a._id === openMenuId);
    if (assignment) {
      setSelectedAssignment(assignment);
      setShowActionModal(type);
    }
    handleCloseMenu();
  };

  // Loading state
  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#8B52FF]" />
      </div>
    );
  }

  // Not found state
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            User Not Found
          </h2>
          <button
            onClick={() => navigate("/admin/users")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B52FF] text-white rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  const selectedAssignmentForMenu = assignments.find(
    (a) => a._id === openMenuId
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <button
          onClick={() => navigate("/admin/users")}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Users
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          <UserProfileCard user={user} formatDate={formatDate} />
        </div>

        {/* Right Column - Admin Actions & Assignments */}
        <div className="lg:col-span-2 space-y-6">
          {/* Admin Actions Section */}
          <AdminActionsCard
            user={user}
            profileCompletion={profileCompletion}
            isProfileIncomplete={isProfileIncomplete}
            isSendingReminder={isSendingReminder}
            isSendingTaskReminder={isSendingTaskReminder}
            isReactivating={isReactivating}
            onSendProfileReminder={handleSendProfileReminder}
            onSendTaskReminder={handleSendTaskReminder}
            onReactivate={handleReactivateUser}
            onShowSuspendModal={() => setShowSuspendModal(true)}
            onShowTerminateModal={() => setShowTerminateModal(true)}
          />

          {/* Assignments Section */}
          <UserAssignmentsList
            assignments={assignments}
            isLoading={assignmentsLoading}
            isUserActive={user.status === "active"}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            onAssignProject={() => setShowAssignModal(true)}
            onOpenMenu={handleOpenMenu}
            formatDate={formatDate}
          />
        </div>

        {/* Dropdown Menu */}
        <AssignmentDropdownMenu
          assignment={selectedAssignmentForMenu || null}
          menuPosition={menuPosition}
          onClose={handleCloseMenu}
          onAction={handleMenuAction}
        />
      </div>

      {/* Modals */}
      <SuspendUserModal
        isOpen={showSuspendModal}
        reason={suspendReason}
        isPending={isSuspending}
        onReasonChange={setSuspendReason}
        onConfirm={handleSuspendUser}
        onClose={() => {
          setShowSuspendModal(false);
          setSuspendReason("");
        }}
      />

      <TerminateUserModal
        isOpen={showTerminateModal}
        reason={terminateReason}
        isPending={isTerminating}
        onReasonChange={setTerminateReason}
        onConfirm={handleTerminateUser}
        onClose={() => {
          setShowTerminateModal(false);
          setTerminateReason("");
        }}
      />

      {showAssignModal && (
        <AssignProjectModal
          userId={userId!}
          onClose={() => setShowAssignModal(false)}
          onAssign={assignProject}
          isPending={isAssigning}
        />
      )}

      {showActionModal && selectedAssignment && (
        <AssignmentActionModal
          type={showActionModal}
          assignment={selectedAssignment}
          onClose={() => {
            setShowActionModal(null);
            setSelectedAssignment(null);
          }}
          onReview={reviewApplication}
          onUpdateStatus={updateStatus}
          onUnassign={unassignProject}
          isReviewing={isReviewing}
          isUpdatingStatus={isUpdatingStatus}
          isUnassigning={isUnassigning}
        />
      )}
    </div>
  );
};

export default UserDetails;
